import { useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { processConsentAfterLogin } from '../../application/use-cases';
import { captureDeviceInfo } from '../../infrastructure/services/DeviceInfoService';
import { createSupabaseConsentRepository } from '../../infrastructure/repositories';
import { createWebCryptoHashGenerator } from '../../infrastructure/utils/hashGenerator';

/**
 * Hook to handle legal document consent
 * Wires up infrastructure implementations with application use cases
 */
export const useConsent = () => {
  const sessionId = useMemo(() => uuidv4(), []);

  const dependencies = useMemo(
    () => ({
      consentRepository: createSupabaseConsentRepository(),
      hashGenerator: createWebCryptoHashGenerator(),
    }),
    [],
  );

  /**
   * Records consent after successful login
   * @param userId - Authenticated user ID
   * @returns true if new consent was recorded
   */
  const recordConsent = useCallback(
    async (userId: string): Promise<boolean> => {
      const deviceInfo = captureDeviceInfo();
      const result = await processConsentAfterLogin({ userId, deviceInfo, sessionId }, dependencies);
      return result.newConsentRecorded;
    },
    [sessionId, dependencies],
  );

  return {
    recordConsent,
    sessionId,
  };
};
